<template>
  <v-container fluid class="py-6">
    <!-- ====== SELECTOR DE PERIODO (A vs B) ====== -->
    <v-card class="pa-4 mb-6" outlined>
      <div class="subtitle-1 font-weight-bold mb-2">Comparativa por Grupos</div>

      <v-row class="mb-2">
        <v-col cols="12" md="4">
          <v-select
            v-model="periodType"
            :items="periodTypes"
            item-text="text"
            item-value="value"
            label="Tipo de periodo"
            dense
            hide-details
          />
        </v-col>
      </v-row>

      <!-- ========== DÍAS (rango) ========== -->
      <template v-if="periodType === 'dias'">
        <v-row>
          <!-- Grupo A -->
          <v-col cols="12" md="6">
            <div class="caption grey--text text--darken-1 mb-1">Grupo A</div>
            <v-menu
              v-model="menus.aDays"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290"
              content-class="calendar-contrast"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  v-on="on"
                  :value="labelRangeDays(groupA)"
                  label="Rango de días"
                  readonly
                  dense
                  clearable
                  @click:clear="clearDays('A')"
                  hide-details
                />
              </template>
              <v-date-picker
                v-model="groupA.daysRange"
                range scrollable no-title
                @change="() => { normalizeDays('A'); ensureBIsAfterA(); menus.aDays = false; }"
              />
            </v-menu>
          </v-col>

          <!-- Grupo B -->
          <v-col cols="12" md="6">
            <div class="caption grey--text text--darken-1 mb-1">Grupo B</div>
            <v-menu
              v-model="menus.bDays"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290"
              content-class="calendar-contrast"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  v-on="on"
                  :value="labelRangeDays(groupB)"
                  label="Rango de días"
                  readonly
                  dense
                  clearable
                  @click:clear="clearDays('B')"
                  hide-details
                />
              </template>
              <v-date-picker
                v-model="groupB.daysRange"
                range scrollable no-title
                @change="() => { normalizeDays('B'); ensureBIsAfterA(); menus.bDays = false; }"
              />
            </v-menu>
          </v-col>
        </v-row>
      </template>

      <!-- ========== SEMANAS (ISO) ========== -->
      <template v-else-if="periodType === 'semanas'">
        <v-row>
          <!-- Grupo A -->
          <v-col cols="12" md="6">
            <div class="caption grey--text text--darken-1 mb-1">Grupo A</div>
            <v-row>
              <v-col cols="12" sm="4">
                <v-select
                  v-model.number="groupA.week.year"
                  :items="years"
                  label="Año"
                  dense
                  hide-details
                  @change="() => { ensureWeekRange('A'); ensureBIsAfterA(); }"
                />
              </v-col>
              <v-col cols="6" sm="4">
                <v-select
                    v-model.number="groupA.week.start"
                    :items="weeksForYear(groupA.week.year)"
                    item-text="text"
                    item-value="value"
                    label="Semana inicio"
                    dense hide-details
                    @change="() => { ensureWeekRange('A'); ensureBIsAfterA(); }"
                />
              </v-col>
              <v-col cols="6" sm="4">
                <v-select
                    v-model.number="groupA.week.end"
                    :items="weeksForYear(groupA.week.year)"
                    item-text="text"
                    item-value="value"
                    label="Semana fin"
                    dense hide-details
                    @change="() => { ensureWeekRange('A'); ensureBIsAfterA(); }"
                />
              </v-col>
            </v-row>
            <div class="caption mt-1">Seleccionado: {{ labelWeeks(groupA) }}</div>
          </v-col>

          <!-- Grupo B -->
          <v-col cols="12" md="6">
            <div class="caption grey--text text--darken-1 mb-1">Grupo B</div>
            <v-row>
              <v-col cols="12" sm="4">
                <v-select
                  v-model.number="groupB.week.year"
                  :items="years"
                  label="Año"
                  dense
                  hide-details
                  @change="() => { ensureWeekRange('B'); ensureBIsAfterA(); }"
                />
              </v-col>
              <v-col cols="6" sm="4">
                <v-select
                  v-model.number="groupB.week.start"
                  :items="weeksForYear(groupB.week.year)"
                  label="Semana inicio"
                  dense
                  hide-details
                  @change="() => { ensureWeekRange('B'); ensureBIsAfterA(); }"
                />
              </v-col>
              <v-col cols="6" sm="4">
                <v-select
                  v-model.number="groupB.week.end"
                  :items="weeksForYear(groupB.week.year)"
                  label="Semana fin"
                  dense
                  hide-details
                  @change="() => { ensureWeekRange('B'); ensureBIsAfterA(); }"
                />
              </v-col>
            </v-row>
            <div class="caption mt-1">Seleccionado: {{ labelWeeks(groupB) }}</div>
          </v-col>
        </v-row>
      </template>

      <!-- ========== MESES ========== -->
      <template v-else-if="periodType === 'meses'">
        <v-row>
          <!-- Grupo A -->
          <v-col cols="12" md="6">
            <div class="caption grey--text text--darken-1 mb-1">Grupo A</div>
            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="menus.aMonthStart"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290"
                  content-class="calendar-contrast"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs" v-on="on"
                      :value="groupA.month.start || ''"
                      label="Mes inicio (YYYY-MM)"
                      readonly dense hide-details
                    />
                  </template>
                  <v-date-picker
                    v-model="groupA.month.start"
                    type="month"
                    class="calendar-contrast"
                    no-title
                    scrollable
                    @change="() => { menus.aMonthStart=false; ensureMonthRange('A'); ensureBIsAfterA(); }"
                  />
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  v-model="menus.aMonthEnd"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290"
                  content-class="calendar-contrast"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs" v-on="on"
                      :value="groupA.month.end || ''"
                      label="Mes fin (YYYY-MM)"
                      readonly dense hide-details
                    />
                  </template>
                  <v-date-picker
                    v-model="groupA.month.end"
                    type="month"
                    class="calendar-contrast"
                    no-title
                    scrollable
                    @change="() => { menus.aMonthEnd=false;   ensureMonthRange('A'); ensureBIsAfterA(); }"
                  />
                </v-menu>
              </v-col>
            </v-row>
            <div class="caption mt-1">Seleccionado: {{ labelMonths(groupA) }}</div>
          </v-col>

          <!-- Grupo B -->
          <v-col cols="12" md="6">
            <div class="caption grey--text text--darken-1 mb-1">Grupo B</div>
            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="menus.bMonthStart"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290"
                  content-class="calendar-contrast"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs" v-on="on"
                      :value="groupB.month.start || ''"
                      label="Mes inicio (YYYY-MM)"
                      readonly dense hide-details
                    />
                  </template>
                  <v-date-picker
                    v-model="groupB.month.start"
                    type="month"
                    class="calendar-contrast"
                    no-title
                    scrollable
                    @change="() => { menus.bMonthStart=false; ensureMonthRange('B'); ensureBIsAfterA(); }"
                  />
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  v-model="menus.bMonthEnd"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290"
                  content-class="calendar-contrast"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs" v-on="on"
                      :value="groupB.month.end || ''"
                      label="Mes fin (YYYY-MM)"
                      readonly dense hide-details
                    />
                  </template>
                  <v-date-picker
                    v-model="groupB.month.end"
                    type="month"
                    class="calendar-contrast"
                    no-title
                    scrollable
                    @change="() => { menus.bMonthEnd=false;   ensureMonthRange('B'); ensureBIsAfterA(); }"
                  />
                </v-menu>
              </v-col>
            </v-row>
            <div class="caption mt-1">Seleccionado: {{ labelMonths(groupB) }}</div>
          </v-col>
        </v-row>
      </template>

      <!-- ========== AÑOS ========== -->
      <template v-else-if="periodType === 'anos'">
        <v-row>
          <!-- Grupo A -->
          <v-col cols="12" md="6">
            <div class="caption grey--text text--darken-1 mb-1">Grupo A</div>
            <v-row>
              <v-col cols="6">
                <v-select
                  v-model.number="groupA.year.start"
                  :items="years"
                  label="Año inicio"
                  dense hide-details
                  @change="() => { ensureYearRange('A'); ensureBIsAfterA(); }"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model.number="groupA.year.end"
                  :items="years"
                  label="Año fin"
                  dense hide-details
                  @change="() => { ensureYearRange('A'); ensureBIsAfterA(); }"
                />
              </v-col>
            </v-row>
            <div class="caption mt-1">Seleccionado: {{ labelYears(groupA) }}</div>
          </v-col>

          <!-- Grupo B -->
          <v-col cols="12" md="6">
            <div class="caption grey--text text--darken-1 mb-1">Grupo B</div>
            <v-row>
              <v-col cols="6">
                <v-select
                  v-model.number="groupB.year.start"
                  :items="years"
                  label="Año inicio"
                  dense hide-details
                  @change="() => { ensureYearRange('B'); ensureBIsAfterA(); }"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model.number="groupB.year.end"
                  :items="years"
                  label="Año fin"
                  dense hide-details
                  @change="() => { ensureYearRange('B'); ensureBIsAfterA(); }"
                />
              </v-col>
            </v-row>
            <div class="caption mt-1">Seleccionado: {{ labelYears(groupB) }}</div>
          </v-col>
        </v-row>
      </template>
    </v-card>
    <v-alert v-if="apiError" type="error" outlined dense class="mb-4">
        {{ apiError }}
    </v-alert>
    <v-skeleton-loader v-if="loading" type="card@2" class="mb-4"></v-skeleton-loader>
    <!-- ====== GRID DE KPIS (demo) ====== -->
    <v-row v-if="kpis.length">
    <v-col
        v-for="(kpi, idx) in kpis"
        :key="idx"
        cols="12"
        md="6"
        lg="6"
    >
        <v-card
        class="pa-5 kpi-card hover:shadow-lg transition"
        outlined
        @click="openModal(idx)"
        role="button"
        >
        <div class="d-flex align-center justify-space-between">
            <!-- Info principal -->
            <div>
            <div class="kpi-title">{{ kpi.title }}</div>

            <!-- Valor actual (Grupo B) -->
            <div class="kpi-value">
            {{ fmt(kpi.general.value) }}
            <span class="kpi-unit">{{ kpi.unit }}</span>
            </div>
            </div>

            <!-- Delta a la derecha -->
            <div class="text-right kpi-delta">
            <div
                :class="[
                deltaSign(kpi) >= 0 ? 'green--text text--darken-2' : 'red--text text--darken-2',
                'kpi-delta-number'
                ]"
                :title="deltaSign(kpi) >= 0 ? 'Mejora' : 'Desmejora'"
            >
                {{ deltaPrefix(kpi) }}{{ fmt(Math.abs(delta(kpi))) }} {{ kpi.unit }}
            </div>
            <div class="kpi-delta-pct">
                {{ deltaPctPrefix(kpi) }}{{ Math.abs(deltaPct(kpi)).toFixed(1) }}%
            </div>
            </div>
        </div>

        <!-- Sub-sección: A vs B lado a lado -->
        <v-divider class="my-4"></v-divider>
        <v-row class="kpi-subgrid" no-gutters>
            <v-col cols="12" sm="6" class="pr-sm-2 mb-3 mb-sm-0">
            <div class="mini-title">Grupo A</div>
            <div class="mini-label truncate">{{ kpi.groupA.label }}</div>
            <div class="mini-value">
                {{ fmt(kpi.groupA.value) }} <span class="mini-unit">{{ kpi.unit }}</span>
            </div>
            </v-col>
            <v-col cols="12" sm="6" class="pl-sm-2">
            <div class="mini-title">Grupo B</div>
            <div class="mini-label truncate">{{ kpi.groupB.label }}</div>
            <div class="mini-value current">
                {{ fmt(kpi.groupB.value) }} <span class="mini-unit">{{ kpi.unit }}</span>
            </div>
            </v-col>
        </v-row>
        </v-card>

        <!-- Modal (igual que ya lo tienes) -->
        <v-dialog v-model="kpi.open" max-width="1100px" persistent>
        <v-card>
            <v-card-title class="d-flex align-center justify-space-between">
            <h2>{{ kpi.title }} — Detalle</h2>
            <v-btn icon @click="kpi.open = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text>
            <v-row class="mb-4">
                <v-col cols="12" md="4">
                <v-card class="pa-3" outlined>
                    <div class="caption grey--text">Grupo A ({{ kpi.groupA.label }})</div>
                    <div class="subtitle-1 font-weight-bold">{{ fmt(kpi.groupA.value) }} {{ kpi.unit }}</div>
                </v-card>
                </v-col>
                <v-col cols="12" md="4">
                <v-card class="pa-3" outlined>
                    <div class="caption grey--text">Grupo B ({{ kpi.groupB.label }})</div>
                    <div class="subtitle-1 font-weight-bold">{{ fmt(kpi.groupB.value) }} {{ kpi.unit }}</div>
                </v-card>
                </v-col>
                <v-col cols="12" md="4">
                <v-card class="pa-3" outlined>
                    <div class="caption grey--text">Δ (B - A)</div>
                    <div
                    :class="[
                        deltaSign(kpi) >= 0 ? 'green--text text--darken-2' : 'red--text text--darken-2',
                        'subtitle-1',
                        'font-weight-bold'
                    ]"
                    >
                    {{ deltaPrefix(kpi) }}{{ fmt(Math.abs(delta(kpi))) }} {{ kpi.unit }}
                    <span class="caption grey--text">
                        ({{ deltaPctPrefix(kpi) }}{{ Math.abs(deltaPct(kpi)).toFixed(1) }}%)
                    </span>
                    </div>
                </v-card>
                </v-col>
            </v-row>

            <v-tabs v-model="kpi.tab" background-color="transparent" grow>
            <v-tab>Resumen</v-tab>
            <v-tab v-if="(kpi.pareto && kpi.pareto.length) || (kpi.paretoAB && kpi.paretoAB.length)">Pareto</v-tab>
            <v-tab v-if="(kpi.evolucion||[]).length">Evolución</v-tab>
            <v-tab v-if="(kpi.tabla||[]).length">Tabla</v-tab>
            <template v-if="kpi._kind === 'parosTop3'">
                <v-tab>Paros x máquina</v-tab>
                <v-tab>Atención</v-tab>
            </template>
            </v-tabs>

            <v-tabs-items v-model="kpi.tab" class="mt-4">

                <!-- Resumen -->
                <v-tab-item>
                    <div class="body-2">
                    Vista general para “{{ kpi.title }}” comparando
                    <strong>{{ kpi.groupA.label }}</strong> vs <strong>{{ kpi.groupB.label }}</strong>.
                    </div>
                </v-tab-item>

                <!-- Pareto -->
                <v-tab-item v-if="(kpi.pareto && kpi.pareto.length) || (kpi.paretoAB && kpi.paretoAB.length)">
                    <apexchart
                    :key="`pareto-${kpi.title}`"
                    type="bar"
                    height="320"
                    :options="paretoOptions(kpi)"
                    :series="paretoSeries(kpi)"
                    />
                </v-tab-item>

                <!-- Evolución -->
                <v-tab-item v-if="(kpi.evolucion||[]).length">
                    <apexchart
                    :key="`evol-${kpi.title}`"
                    type="line"
                    height="320"
                    :options="evolOptions(kpi)"
                    :series="evolSeries(kpi)"
                    />
                </v-tab-item>


                <!-- Tabla -->
                <v-tab-item v-if="(kpi.tabla||[]).length">
                    <v-data-table :items="kpi.tabla || []" :headers="tablaHeaders(kpi)" dense>
                    <template #item.horasInicio="{ item }">{{ fmt(item.horasInicio) }}</template>
                    <template #item.horasFin="{ item }">{{ fmt(item.horasFin) }}</template>
                    <template #item.variacionHoras="{ item }">{{ fmt(item.variacionHoras) }}</template>
                    <template #item.mejoraPct="{ item }">
                        <span :class="item.mejoraPct >= 0 ? 'green--text text--darken-2' : 'red--text text--darken-2'">
                        {{ item.mejoraPct.toFixed(1) }}%
                        </span>
                    </template>
                    </v-data-table>
                </v-tab-item>

                <!-- Paros x máquina (SOLO para parosTop3) -->
                <template v-if="kpi._kind === 'parosTop3'">
                    <v-tab-item>
                    <v-alert v-if="kpi._errorParos" type="error" outlined dense class="mb-3">
                        {{ kpi._errorParos }}
                    </v-alert>
                    <v-skeleton-loader v-if="kpi._loadingParos" type="card@2"></v-skeleton-loader>

                    <template v-else>
                        <div class="subtitle-1 font-weight-bold mb-2">
                        Top {{
                        ((kpi.paros && kpi.paros.maquinas) ? kpi.paros.maquinas : []).length
                        }} máquinas por tiempo de paro
                        </div>

                        <v-row>
                        <v-col
                            v-for="maq in ((kpi.paros && kpi.paros.maquinas) ? kpi.paros.maquinas : [])"
                            :key="maq.id"
                            cols="12" md="6" lg="4"
                        >
                            <v-card class="pa-3" outlined>
                            <div class="d-flex align-center justify-space-between mb-2">
                                <div class="font-weight-bold">{{ maq.nombre }}</div>
                                <div class="caption grey--text">No clasif.: {{ Number(maq.pctNoClasif).toFixed(1) }}%</div>
                            </div>

                            <!-- 1) Pareto -->
                            <apexchart
                                :key="`pxm-pareto-${maq.id}`"
                                type="bar"
                                height="240"
                                :options="pxmParetoOptions(kpi, maq)"
                                :series="pxmParetoSeries(maq)"
                            />

                            <v-divider class="my-3"></v-divider>

                            <!-- 2) Evolución paro total + tendencia -->
                            <apexchart
                                :key="`pxm-evol-${maq.id}`"
                                type="line"
                                height="220"
                                :options="pxmEvolOptions(kpi, maq)"
                                :series="pxmEvolSeries(maq)"
                            />

                            <v-divider class="my-3"></v-divider>

                            <!-- 3) Tabla Top-5 -->
                            <v-data-table :items="maq.tablaTop5 || []" :headers="pxmTablaHeaders"
                                            dense hide-default-footer :items-per-page="5">
                                <template #item.horasInicio="{ item }">{{ fmt(item.horasInicio) }}</template>
                                <template #item.horasFin="{ item }">{{ fmt(item.horasFin) }}</template>
                                <template #item.variacionHoras="{ item }">{{ fmt(item.variacionHoras) }}</template>
                                <template #item.mejoraPct="{ item }">
                                <span :class="item.mejoraPct >= 0 ? 'red--text text--darken-2' : 'green--text text--darken-2'">
                                    {{ item.mejoraPct.toFixed(1) }}%
                                </span>
                                </template>
                            </v-data-table>

                            <v-divider class="my-3"></v-divider>

                            <!-- 4) Evolución top-3 causas -->
                            <apexchart
                                :key="`pxm-causas-${maq.id}`"
                                type="line"
                                height="220"
                                :options="pxmEvolCausasOptions(kpi, maq)"
                                :series="pxmEvolCausasSeries(maq)"
                            />
                            </v-card>
                        </v-col>
                        </v-row>
                    </template>
                    </v-tab-item>

                    <!-- Atención -->
                    <v-tab-item>
                    <v-card outlined class="pa-5">
                        <div class="subtitle-1 font-weight-bold mb-2">Tiempo de atención de paros</div>
                        <div class="body-2 grey--text">Proximamente...</div>
                    </v-card>
                    </v-tab-item>
                </template>
            </v-tabs-items>
            </v-card-text>

            <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" dark @click="kpi.open = false">Cerrar</v-btn>
            </v-card-actions>
        </v-card>
        </v-dialog> 
    </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
// paleta fija (elige las que quieras)
const PALETTE = [
  '#2563eb', // azul
  '#10b981', // verde
  '#f59e0b', // ámbar
  '#ef4444', // rojo
  '#8b5cf6', // violeta
  '#06b6d4', // cian
  '#f97316', // naranja
  '#22c55e', // verde claro
];
const baseName = n => String(n||'').replace(/\s+—\s*tendencia/i,'').trim();
const isTrend  = n => /tendencia/i.test(String(n||''));

function hexToRgba(hex, alpha=0.45){
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if(!m) return hex;
  const r = parseInt(m[1],16), g = parseInt(m[2],16), b = parseInt(m[3],16);
  return `rgba(${r},${g},${b},${alpha})`;
}
export default {
  name: "ComparativaReporteKPI",
  data() {
    const today = new Date();
    const yearNow = today.getFullYear();

    // Grupos demo (inicial: días)
    const groupA = {
      label: "Últimos 5 días",
      // Días (range)
      daysRange: [], // ['YYYY-MM-DD','YYYY-MM-DD']
      // Semanas (ISO)
      week: { year: yearNow, start: 35, end: 37 },
      // Meses
      month: { start: `${yearNow}-08`, end: `${yearNow}-09` },
      // Años
      year: { start: yearNow - 1, end: yearNow - 1 },
    };
    const groupB = {
      label: "Hoy",
      daysRange: [],
      week: { year: yearNow, start: 38, end: 38 },
      month: { start: `${yearNow}-09`, end: `${yearNow}-09` },
      year: { start: yearNow, end: yearNow },
    };

    return {
      // Tipo de periodo
      _fetchDebounce: null,
      loading: false,
      apiError: null,
      kpis: [], // <- inicia vacío; dejaremos que lo llene el backend
      periodType: "dias",
      periodTypes: [
        { text: "Días", value: "dias" },
        { text: "Semanas", value: "semanas" },
        { text: "Meses", value: "meses" },
        { text: "Años", value: "anos" },
      ],
      menus: {
        aDays: false, bDays: false,
        aMonthStart: false, aMonthEnd: false,
        bMonthStart: false, bMonthEnd: false,
      },
      years: this.buildYears(yearNow - 5, yearNow + 1),
      groupA, 
      groupB,
    };
  },

  watch: {
        periodType () {
            this.syncKpiLabels();
            this.ensureBIsAfterA();
            this.fetchComparativa();
        },
        groupA: {
            deep: true,
            handler () {
            this.syncKpiLabels();
            this.ensureBIsAfterA();
            this.fetchComparativa();
            }
        },
        groupB: {
            deep: true,
            handler () {
            this.syncKpiLabels();
            this.ensureBIsAfterA();
            this.fetchComparativa();
            }
        }
    },

  methods: {
    /* ---------- Helpers de rango/labels ---------- */

    buildYears(start, end) {
      const ys = [];
      for (let y = start; y <= end; y++) ys.push(y);
      return ys;
    },
    async loadParosIntoKpi(kpi) {
    try {
        kpi._loadingParos = true;
        kpi._errorParos = null;

        const payload = this.buildRequestPayload();
        const { data } = await axios.post('/Comparativa/ComparativaParosPorMaquina', payload);

        const pick = (o, ...keys) => keys.map(k => o?.[k]).find(v => v !== undefined);
        const parsed = {
        unit: pick(data, 'unit', 'Unit') || 'hrs',
        bucketMode: pick(data, 'bucketMode', 'BucketMode') || 'semanas',
        totalA: Number(pick(data, 'totalA', 'TotalA') || 0),
        totalB: Number(pick(data, 'totalB', 'TotalB') || 0),
        maquinas: (pick(data, 'maquinas', 'Maquinas') || []).map(m => ({
            id: m.id ?? m.Id,
            nombre: m.nombre ?? m.Nombre,
            paroHrs: Number(m.paroHrs ?? m.ParoHrs ?? 0),
            pctNoClasif: Number(m.pctNoClasif ?? m.PctNoClasif ?? 0),
            pareto: m.pareto ?? m.Pareto ?? [],
            evolParo: m.evolParo ?? m.EvolParo ?? [],
            tablaTop5: m.tablaTop5 ?? m.TablaTop5 ?? [],
            evolTop3: m.evolTop3 ?? m.EvolTop3 ?? [],
        })),
        };

        // Actualiza los totales del KPI (A, B y General)
        kpi.paros = parsed;
        kpi.unit = parsed.unit;
        kpi.groupA.value = parsed.totalA;
        kpi.groupB.value = parsed.totalB;
        kpi.general.value = parsed.totalB;
    } catch (e) {
        console.error(e);
        kpi._errorParos = 'No se pudo cargar Paros x máquina.';
        kpi.paros = { unit:'hrs', bucketMode:'semanas', maquinas:[] };
    } finally {
        kpi._loadingParos = false;
    }
    },
    pxmParetoSeries(maq) {
    return [{ name: 'Horas', data: (maq.pareto || []).map(p => Number(p.horas ?? p.Horas ?? 0)) }];
    },
    pxmParetoOptions(kpi, maq) {
    const unit = kpi?.paros?.unit || kpi?.unit || 'hrs';
    return {
        chart:{ toolbar:{ show:false } },
        xaxis:{ categories:(maq.pareto||[]).map(p => p.label ?? p.Label ?? '') },
        plotOptions:{ bar:{ columnWidth:'45%', dataLabels:{ position:'top' } } },
        dataLabels:{
        enabled:true, offsetY:-12,
        style:{ colors:['#111'], fontSize:'12px', fontWeight:700 },
        formatter:v => `${Number(v).toLocaleString('en-US',{maximumFractionDigits:2})} ${unit}`
        }
    };
    },
    pxmEvolSeries(maq) {
    return (maq.evolParo || []).map(s => ({
        name: s.label ?? s.Label,
        data: (s.puntos ?? s.Puntos ?? []).map(p => Number(p.horas ?? p.Horas ?? 0)),
    }));
    },
    pxmEvolOptions(kpi, maq) {
    const cats = (maq.evolParo?.[0]?.puntos || maq.evolParo?.[0]?.Puntos || []).map(p => p.periodoKey ?? p.PeriodoKey);
    return {
        chart:{ toolbar:{ show:false } },
        xaxis:{ categories: cats },
        stroke:{ width: [3,2], curve:'smooth', dashArray:[0,6] },
        dataLabels:{ enabled:false },
        markers:{ size:3 },
        legend:{ show:true },
    };
    },
    pxmEvolCausasSeries(maq) {
    return (maq.evolTop3 || []).map(s => ({
        name: s.label ?? s.Label,
        data: (s.puntos ?? s.Puntos ?? []).map(p => Number(p.horas ?? p.Horas ?? 0)),
    }));
    },
    pxmEvolCausasOptions(kpi, maq) {
    const cats = (maq.evolTop3?.[0]?.puntos || maq.evolTop3?.[0]?.Puntos || []).map(p => p.periodoKey ?? p.PeriodoKey);
    return {
        chart:{ toolbar:{ show:false } },
        xaxis:{ categories: cats },
        stroke:{ curve:'smooth' },
        dataLabels:{ enabled:false },
        markers:{ size:3 },
        legend:{ show:true },
    };
    },
    pxmTablaHeaders: [
    { text: 'Causa', value: 'label' },
    { text: 'Mitad 1 (hrs)', value: 'horasInicio' },
    { text: 'Mitad 2 (hrs)', value: 'horasFin' },
    { text: 'Δ (hrs)', value: 'variacionHoras' },
    { text: 'Cambio (%)', value: 'mejoraPct' },
    ],

    // ===== DÍAS
    normalizeDays(side) {
      const g = side === 'A' ? this.groupA : this.groupB;
      const arr = Array.isArray(g.daysRange) ? g.daysRange.slice(0, 2) : [];
      if (arr.length === 2 && arr[0] && arr[1] && arr[0] > arr[1]) arr.reverse();
      g.daysRange = arr;
    },
    clearDays(side) {
      const g = side === 'A' ? this.groupA : this.groupB;
      g.daysRange = [];
    },
    labelRangeDays(g) {
      const r = g.daysRange || [];
      if (Array.isArray(r) && r.length === 2 && r[0] && r[1]) return `${r[0]} → ${r[1]}`;
      if (Array.isArray(r) && r.length === 1 && r[0]) return `${r[0]} → ${r[0]}`;
      return '';
      // (vacío hasta seleccionar rango)
    },

    // ===== SEMANAS (ISO)
    weeksForYear(year) {
        const n = this.isoWeeksInYear(year);
        const arr = [];
        for (let w = 1; w <= n; w++) {
        const s = this.isoWeekStartDate(year, w);
        const e = this.isoWeekEndDate(year, w);
        const text = `Semana ${String(w).padStart(2,'0')} (${this.fmtISO(s)} → ${this.fmtISO(e)})`;
        arr.push({ text, value: w });
        }
        return arr;
    },

    ensureWeekRange(side) {
        const g = side === 'A' ? this.groupA : this.groupB;
        if (g.week.start > g.week.end) [g.week.start, g.week.end] = [g.week.end, g.week.start];
    },
    labelWeeks(g) {
        const { year, start, end } = g.week || {};
        if (!year || !start || !end) return '';
        const s1 = this.isoWeekStartDate(year, start), e1 = this.isoWeekEndDate(year, start);
        const s2 = this.isoWeekStartDate(year, end),   e2 = this.isoWeekEndDate(year, end);
        return `(${this.fmtISO(s1)} a ${this.fmtISO(e2)})`;
    },
    isoWeeksInYear(y) {
        const thursday = d => {
        const day = (d.getUTCDay() + 6) % 7; // 0=Mon..6=Sun
        d.setUTCDate(d.getUTCDate() - day + 3); // Thursday
        return d;
        };
        const dec28 = new Date(Date.UTC(y, 11, 28));
        const week = 1 + Math.floor((thursday(dec28) - new Date(Date.UTC(y, 0, 4))) / (7 * 24 * 3600 * 1000));
        return week; // 52 o 53
    },
    isoWeekStartDate(year, week) {
        // ISO: semana 1 es la que contiene el 4 de enero
        const simple = new Date(Date.UTC(year, 0, 4));
        const dayOfWeek = (simple.getUTCDay() + 6) % 7; // 0=Mon..6=Sun
        const mondayWeek1 = new Date(simple);
        mondayWeek1.setUTCDate(simple.getUTCDate() - dayOfWeek); // lunes de la semana 1
        const mondayTarget = new Date(mondayWeek1);
        mondayTarget.setUTCDate(mondayWeek1.getUTCDate() + (week - 1) * 7);
        return mondayTarget; // Monday
    },
    isoWeekEndDate(year, week) {
        const start = this.isoWeekStartDate(year, week);
        const sunday = new Date(start);
        sunday.setUTCDate(start.getUTCDate() + 6);
        return sunday;
    },
    fmtISO(d) {
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    },

    // ===== MESES
    ensureMonthRange(side) {
      const g = side === 'A' ? this.groupA : this.groupB;
      const s = g.month.start, e = g.month.end;
      if (s && e && s > e) [g.month.start, g.month.end] = [e, s];
    },
    labelMonths(g) {
      const { start, end } = g.month || {};
      if (start && end) return `${start} → ${end}`;
      if (start) return `${start}`;
      return '';
    },

    // ===== AÑOS
    ensureYearRange(side) {
      const g = side === 'A' ? this.groupA : this.groupB;
      if (g.year.start && g.year.end && g.year.start > g.year.end) [g.year.start, g.year.end] = [g.year.end, g.year.start];
    },
    labelYears(g) {
      const { start, end } = g.year || {};
      if (start && end) return `${start} → ${end}`;
      if (start) return `${start}`;
      return '';
    },

    // ========= Sincroniza labels visibles en las tarjetas KPI =========
    currentLabel(g) {
      switch (this.periodType) {
        case 'dias':    return this.labelRangeDays(g) || (g === this.groupA ? 'Días A' : 'Días B');
        case 'semanas': return this.labelWeeks(g)      || (g === this.groupA ? 'Semanas A' : 'Semanas B');
        case 'meses':   return this.labelMonths(g)     || (g === this.groupA ? 'Meses A' : 'Meses B');
        case 'anos':    return this.labelYears(g)      || (g === this.groupA ? 'Años A' : 'Años B');
        default: return '';
      }
    },
    syncKpiLabels() {
      const la = this.currentLabel(this.groupA);
      const lb = this.currentLabel(this.groupB);
      this.kpis.forEach(k => {
        k.groupA.label = la || this.groupA.label;
        k.groupB.label = lb || this.groupB.label;
      });
    },

    /* ---------- Métricas KPI (demo) ---------- */
    openModal(idx) {
    const k = this.kpis[idx];
    k.tab = 0;
    k.open = true;

    if (k._kind === 'parosTop3' && !k.paros && !k._loadingParos) {
        this.loadParosIntoKpi(k);
    }
    },
    delta(kpi) { return Number(kpi.groupB.value || 0) - Number(kpi.groupA.value || 0); },
    deltaSign(kpi) { return Math.sign(this.delta(kpi)); },
    deltaPrefix(kpi) { return this.deltaSign(kpi) >= 0 ? '+' : '−'; },
    deltaPct(kpi) {
      const a = Number(kpi.groupA.value || 0);
      return a <= 0 ? 0 : (this.delta(kpi) / a) * 100;
    },
    deltaPctPrefix(kpi) { return this.deltaSign(kpi) >= 0 ? '+' : '−'; },
    fmt(v) { return Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 }); },

    /* ---------- Gráficas (demo) ---------- */
    paretoSeries(kpi) {
        const ab = kpi.paretoAB || kpi.ParetoAB;
        if (Array.isArray(ab) && ab.length) {
            const toNum = v => Number.isFinite(+v) ? +v : 0;
            return [
            { name: 'Grupo A', data: ab.map(p => toNum(p.horasA ?? p.HorasA)) },
            { name: 'Grupo B', data: ab.map(p => toNum(p.horasB ?? p.HorasB)) },
            ];
        }
        return [{ name: 'Horas', data: (kpi.pareto || []).map(p => Number(p.horas ?? p.Horas ?? 0)) }];
    },

    paretoOptions(kpi) {
        const ab = kpi.paretoAB || kpi.ParetoAB;
        const cats = Array.isArray(ab) && ab.length
            ? ab.map(p => p.label ?? p.Label)
            : (kpi.pareto || []).map(p => p.label ?? p.Label);
        const unit = kpi.unit || 'hrs';
        return {
            chart: { toolbar: { show: false } },
            xaxis: { categories: cats || [] },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '45%',
                    dataLabels: { position: 'top' } // ← arriba
                }
            },
            dataLabels: {
            enabled: true,
            offsetY: -15, // empuja el texto hacia arriba de la barra
            style: { colors: ['#111'], fontSize: '12px', fontWeight: 700 }, // ← negro
            formatter: v => `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${kpi.unit}`,
            },
            grid: { padding: { top: 12 } },
            tooltip: { y: { formatter: v => `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${unit}` } },
        };
    },
    evolSeries(kpi) {
      return (kpi.evolucion || []).map(s => ({
        name: s.label,
        data: (s.puntos || []).map(p => Number(p.horas || 0)),
      }));
    },
    evolOptions(kpi) {
        const sDefs = (kpi.evolucion || []);           // [{label, puntos:[...]}]
        const cats  = (sDefs[0]?.puntos || []).map(p => p.periodoKey);
        const names = sDefs.map(s => s.label || '');
        const unit  = kpi.unit || 'hrs';

        // 1) asigna índice de color por causa base (solo a "normal")
        const idxByBase = {};
        let next = 0;
        names.forEach(n => {
            const b = baseName(n);
            if (!isTrend(n) && idxByBase[b] == null) idxByBase[b] = next++;
        });

        // 2) genera colores: normal = sólido, tendencia = mismo color aclarado
        const colors = names.map(n => {
            const b = baseName(n);
            const idx = (idxByBase[b] ?? (idxByBase[b] = next++)) % PALETTE.length;
            const base = PALETTE[idx];
            return isTrend(n) ? hexToRgba(base, 0.45) : base;
        });

        // estilos: normal más gruesa; tendencia discontinua
        const strokeWidths = names.map(n => isTrend(n) ? 2 : 3);
        const dashArray    = names.map(n => isTrend(n) ? 6 : 0);

        return {
            chart: { toolbar: { show: false } },
            colors,                             // ← aquí va la magia
            xaxis: { categories: cats },
            yaxis: [
            { title: { text: 'Horas (normal)' }, decimalsInFloat: 2 },
            { opposite: true, show: true, title: { text: 'Horas (tendencia)' }, decimalsInFloat: 2 }
            ],
            stroke: { width: strokeWidths, curve: 'smooth', dashArray },
            dataLabels: { enabled: false },
            markers: { size: 3, colors },       // marcadores con el mismo color
            tooltip: {
                shared: true,
                custom: ({ series, dataPointIndex, w }) => {
                    const sDefs = w.config.series;
                    const isTrend = n => /tendencia/i.test(String(n || ''));
                    const cats = w.config.xaxis.categories || [];

                    const map = {};
                    sDefs.forEach((s, i) => {
                    const base = String(s.name || '').replace(/\s+—\s*tendencia/i, '').trim();
                    const trend = isTrend(s.name);
                    map[base] ??= { normal: null, trend: null };
                    if (trend) map[base].trend = i; else map[base].normal = i;
                    });

                    const rows = Object.keys(map).map(base => {
                    const ni = map[base].normal;
                    const ti = map[base].trend;
                    const vN = ni != null ? series[ni][dataPointIndex] : null;
                    const vT = ti != null ? series[ti][dataPointIndex] : null;
                    return { base, vN, vT };
                    }).sort((a,b) => (b.vN ?? 0) - (a.vN ?? 0));

                    // 👇 ¿Hay alguna tendencia numérica en este x?
                    const hasTrend = rows.some(r => typeof r.vT === 'number' && !isNaN(r.vT));

                    const unit = kpi.unit || 'hrs';
                    const xLabel = cats[dataPointIndex] ?? '';
                    const escapeHtml = s => String(s)
                    .replace(/&/g,'&amp;').replace(/</g,'&lt;')
                    .replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');
                    const fmt = v => (v == null || isNaN(v))
                    ? '—'
                    : `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${unit}`;

                    let html = `
                    <div class="tt2">
                        <div class="tt2-head">${escapeHtml(xLabel)}</div>
                        <div class="tt2-grid ${hasTrend ? '' : 'cols2'}">
                        <div class="tt2-h tt2-causa">Causa</div>
                        <div class="tt2-h tt2-num">Normal</div>
                        ${hasTrend ? '<div class="tt2-h tt2-num">Tendencia</div>' : ''}
                    `;

                    rows.forEach(r => {
                    html += `
                        <div class="tt2-causa">${escapeHtml(r.base)}</div>
                        <div class="tt2-num">${fmt(r.vN)}</div>
                        ${hasTrend ? `<div class="tt2-num">${fmt(r.vT)}</div>` : ''}
                    `;
                    });

                    html += `</div></div>`;
                    return html;
                }
            },
            legend: { show: true }
        };
    },
    tablaHeaders(kpi) {
      return [
        { text: 'Item', value: 'label' },
        { text: `Horas ${kpi.groupA.label}`, value: 'horasInicio' },
        { text: `Horas ${kpi.groupB.label}`, value: 'horasFin' },
        { text: 'Δ (hrs)', value: 'variacionHoras' },
        { text: 'Mejora (%)', value: 'mejoraPct' },
      ];
    },
    // ===== utilidades fecha =====
    toDate (s) {
        // "YYYY-MM-DD" -> Date (local, 00:00)
        const [Y,M,D] = String(s||'').split('-').map(Number);
        return new Date(Y, (M||1)-1, D||1);
    },
    fmtISOlocal (d) {
        const y = d.getFullYear();
        const m = String(d.getMonth()+1).padStart(2,'0');
        const dd = String(d.getDate()).padStart(2,'0');
        return `${y}-${m}-${dd}`;
    },
    addDays (s, n=1) {
        const d = this.toDate(s);
        d.setDate(d.getDate()+n);
        return this.fmtISOlocal(d);
    },
    // compara "YYYY-MM" de forma segura
    cmpYM (a,b) { return String(a||'').localeCompare(String(b||'')); },
    // convierte año+semana en un número comparable (mismo año) p.e. 2025*100 + 37
    ywNum (y,w) { return (Number(y)||0)*100 + (Number(w)||0); },

    // ===== validaciones por tipo =====
    ensureBGT_Days () {
        const A = this.groupA.daysRange || [];
        const B = this.groupB.daysRange || [];
        if (A.length && B.length) {
            const aEnd = A[1] || A[0];
            const bStart = B[0];
            // Si B comienza en o antes que A termina -> mueve B para que empiece un día después
            if (bStart && aEnd && this.toDate(bStart) <= this.toDate(aEnd)) {
            const newStart = this.addDays(aEnd, 1);
            const span = (B[1] && B[0]) ? (this.toDate(B[1]) - this.toDate(B[0]))/(1000*3600*24) : 0;
            const newEnd = span>0 ? this.addDays(newStart, span) : newStart;
            this.groupB.daysRange = [newStart, newEnd];
            }
        }
    },
    ensureBGT_Weeks () {
        const A = this.groupA.week, B = this.groupB.week;
        if (!A || !B) return;

        // 1) Año B >= Año A
        if (B.year < A.year) B.year = A.year;

        // 2) Si es el mismo año: semana B.start > A.end (estricto)
        if (B.year === A.year) {
            const aEnd = this.ywNum(A.year, A.end || A.start);
            let bStart = this.ywNum(B.year, B.start || B.end || 1);
            if (bStart <= aEnd) {
            const nWeeks = this.isoWeeksInYear(B.year);
            const next = (A.end || A.start) + 1;
            B.start = Math.min(next, nWeeks);
            if (!B.end || B.end < B.start) B.end = B.start;
            bStart = this.ywNum(B.year, B.start);
            // si A ocupa las últimas semanas y next se salió, ya está “>”, pero al borde.
            }
        }
    },
    ensureBGT_Months () {
        const A = this.groupA.month || {}, B = this.groupB.month || {};
        if (!A.start && !A.end) return; if (!B.start && !B.end) return;
        const aEnd = A.end || A.start; // "YYYY-MM"
        const bStart = B.start || B.end;
        if (aEnd && bStart && this.cmpYM(bStart, aEnd) <= 0) {
            // mueve B para que empiece el mes siguiente a aEnd
            const [y,m] = aEnd.split('-').map(Number);
            const dt = new Date(y, (m||1)-1, 1);
            dt.setMonth(dt.getMonth()+1);
            const y2 = dt.getFullYear(), m2 = String(dt.getMonth()+1).padStart(2,'0');
            const start = `${y2}-${m2}`;
            // conserva el “span” en meses si existe
            let span = 0;
            if (B.start && B.end) {
            const ds = new Date(Number(B.start.slice(0,4)), Number(B.start.slice(5))-1, 1);
            const de = new Date(Number(B.end.slice(0,4)),   Number(B.end.slice(5))-1,   1);
            span = (de.getFullYear()-ds.getFullYear())*12 + (de.getMonth()-ds.getMonth());
            }
            let end = start;
            if (span>0) {
            const de = new Date(y2, Number(m2)-1, 1);
            de.setMonth(de.getMonth()+span);
            end = `${de.getFullYear()}-${String(de.getMonth()+1).padStart(2,'0')}`;
            }
            this.groupB.month.start = start;
            this.groupB.month.end   = end;
        }
    },
    ensureBGT_Years () {
        const A = this.groupA.year || {}, B = this.groupB.year || {};
        const aEnd = A.end || A.start;
        if (aEnd == null) return;
        if (B.start != null && B.start <= aEnd) {
            const span = (B.end != null && B.start != null) ? (B.end - B.start) : 0;
            const start = aEnd + 1;
            const end = start + Math.max(span, 0);
            this.groupB.year.start = start;
            this.groupB.year.end   = end;
        } else if (B.end != null && B.end <= aEnd) {
            // si solo hay end, empújalo
            this.groupB.year.end = aEnd + 1;
            if (B.start == null) this.groupB.year.start = this.groupB.year.end;
        }
    },

    // selector general
    ensureBIsAfterA () {
        switch (this.periodType) {
            case 'dias':    this.ensureBGT_Days();   break;
            case 'semanas': this.ensureBGT_Weeks();  break;
            case 'meses':   this.ensureBGT_Months(); break;
            case 'anos':    this.ensureBGT_Years();  break;
        }
    },

    buildGroupsPayload () {
        // Convierte los selectores A/B al formato del request del backend
        const toGroup = (g) => ({
            Days: Array.isArray(g.daysRange) && g.daysRange.length
            ? { Start: g.daysRange[0], End: g.daysRange[1] || g.daysRange[0] }
            : null,
            Week: g.week?.year && (g.week.start || g.week.end)
            ? { Year: g.week.year, Start: g.week.start || g.week.end, End: g.week.end || g.week.start }
            : null,
            Month: (g.month?.start || g.month?.end)
            ? { Start: g.month.start || g.month.end, End: g.month.end || g.month.start } // "YYYY-MM"
            : null,
            Year: (g.year?.start || g.year?.end)
            ? { Start: g.year.start || g.year.end, End: g.year.end || g.year.start }
            : null,
        });

        return {
            GroupA: toGroup(this.groupA),
            GroupB: toGroup(this.groupB),
        };
    },
    buildRequestPayload () {
        return {
            PeriodType: this.periodType,        // 'dias' | 'semanas' | 'meses' | 'anos'
            ...this.buildGroupsPayload(),       // GroupA / GroupB
            Filters: {
            // rellena según tus selects reales en la pantalla (si no los tienes aún, déjalo vacío)
            IdArea: this.idArea || null,
            IdMaquina: this.idMaquina || null,
            IdsMaquinaIns: this.idsMaquinaIns || null,
            IdParoTipo: this.idParoTipo || null,
            },
            // Si más adelante usas timezone, agrégalo aquí
        };
    },
    async fetchComparativa () {
        clearTimeout(this._fetchDebounce);
        this._fetchDebounce = setTimeout(async () => {
            if (!this.selectionsValid()) { this.kpis = []; this.apiError = null; return; }

            try {
            this.loading = true;
            this.apiError = null;

            const payload = this.buildRequestPayload();
            const { data } = await axios.post("/Comparativa/ComparativaGeneral", payload);

            // helper para elegir camelCase o PascalCase
            const pick = (o, ...keys) => keys.map(k => o?.[k]).find(v => v !== undefined);

            const arr = [];
            const mttr = data?.mttr || data?.MTTR || {};
            const mtbf = data?.mtbf || data?.MTBF || {};

            if (mttr && (mttr.general || mttr.General || mttr.groupA || mttr.GroupA)) {
                arr.push({
                title: "MTTR (Mean Time To Repair)",
                unit: pick(mttr, "unit", "Unit") || "hrs",
                general: pick(mttr, "general", "General") || { label: "", value: 0 },
                groupA:  pick(mttr, "groupA", "GroupA")   || { label: "", value: 0 },
                groupB:  pick(mttr, "groupB", "GroupB")   || { label: "", value: 0 },
                pareto:  pick(mttr, "pareto", "Pareto")   || [],
                evolucion: pick(mttr, "evolucion", "Evolucion") || [],
                tabla:   pick(mttr, "tabla", "Tabla")     || [],
                open: false, tab: 0,
                });
            }

            if (mtbf && (mtbf.general || mtbf.General || mtbf.groupA || mtbf.GroupA)) {
                arr.push({
                title: "MTBF (Mean Time Between Failures)",
                unit: pick(mtbf, "unit", "Unit") || "hrs",
                general: pick(mtbf, "general", "General") || { label: "", value: 0 },
                groupA:  pick(mtbf, "groupA", "GroupA")   || { label: "", value: 0 },
                groupB:  pick(mtbf, "groupB", "GroupB")   || { label: "", value: 0 },
                pareto:  pick(mtbf, "pareto", "Pareto")   || [],
                evolucion: pick(mtbf, "evolucion", "Evolucion") || [],
                tabla:   pick(mtbf, "tabla", "Tabla")     || [],
                open: false, tab: 0,
                });
            }

            const { data: paros } = await axios.post("/Comparativa/ComparativaParosNiveles", payload);
            if (paros && (paros.general || paros.General)) {
            arr.push({
                title: "Tiempo de Paro por Causas",
                unit: pick(paros, "unit", "Unit") || "hrs",
                general: pick(paros, "general", "General"),
                groupA:  pick(paros, "groupA", "GroupA"),
                groupB:  pick(paros, "groupB", "GroupB"),
                pareto:  pick(paros, "pareto", "Pareto")   || [],
                paretoAB: pick(paros, "paretoAB", "ParetoAB") || [],
                evolucion: pick(paros, "evolucion", "Evolucion") || [],
                tabla:   pick(paros, "tabla", "Tabla")     || [],
                open: false,
                tab: 0,
            });
            }

            // después de cargar MTTR/MTBF/Paros por Causas...
            try {
            const payload = this.buildRequestPayload();
            const { data: pmaqRaw } = await axios.post('/Comparativa/ComparativaParosPorMaquina', payload);
            const pick = (o, ...keys) => keys.map(k => o?.[k]).find(v => v !== undefined);

            const unit    = pick(pmaqRaw, 'unit', 'Unit') || 'hrs';
            const totalA  = Number(pick(pmaqRaw, 'totalA', 'TotalA')) || 0;
            const totalB  = Number(pick(pmaqRaw, 'totalB', 'TotalB')) || 0;
            const maquinas= pick(pmaqRaw, 'maquinas', 'Maquinas') || [];
            const bucket  = pick(pmaqRaw, 'bucketMode', 'BucketMode') || 'semanas';

            // KPI: Paros — Top 3 Máquinas (placeholder; se llenará al abrir el modal)
            // 👉 KPI #4 (idéntico a los otros)
            arr.push({
                _kind: 'parosTop3',                            // flag para tabs especiales
                title: 'Paros Maquina por Tiempo',
                unit: unit,
                general: { label: 'Top 3', value: totalB+totalA },  // valor grande
                groupA:  { label: this.currentLabel(this.groupA), value: totalA },
                groupB:  { label: this.currentLabel(this.groupB), value: totalB },

                // Detalle para las sub-pestañas de ESTE KPI
                parosData: {
                unit: unit,
                bucketMode: bucket,
                maquinas: maquinas.map(m => ({
                    id: m.id ?? m.Id,
                    nombre: m.nombre ?? m.Nombre,
                    paroHrs: m.paroHrs ?? m.ParoHrs ?? 0,
                    pctNoClasif: m.pctNoClasif ?? m.PctNoClasif ?? 0,
                    pareto: m.pareto ?? m.Pareto ?? [],
                    evolParo: m.evolParo ?? m.EvolParo ?? [],
                    tablaTop5: m.tablaTop5 ?? m.TablaTop5 ?? [],
                    evolTop3: m.evolTop3 ?? m.EvolTop3 ?? []
                }))
                },
                open: false,
                tab: 0
            });
            } catch (e) {
            console.error('Paros x máquina:', e);
            // si falla, no agregamos el KPI y el resto sigue funcionando
            }

            this.kpis = arr;
            } catch (err) {
            console.error(err);
            this.apiError = "No se pudo cargar la comparativa.";
            this.kpis = [];
            } finally {
            this.loading = false;
            }
        }, 250);
    },
    selectionsValid () {
        switch (this.periodType) {
        case 'dias':
            return Array.isArray(this.groupA.daysRange) && this.groupA.daysRange.length >= 1 &&
                Array.isArray(this.groupB.daysRange) && this.groupB.daysRange.length >= 1;
        case 'semanas':
            return !!(this.groupA.week?.year && (this.groupA.week.start || this.groupA.week.end)) &&
                !!(this.groupB.week?.year && (this.groupB.week.start || this.groupB.week.end));
        case 'meses':
            return !!(this.groupA.month?.start || this.groupA.month?.end) &&
                !!(this.groupB.month?.start || this.groupB.month?.end);
        case 'anos':
            return !!(this.groupA.year?.start || this.groupA.year?.end) &&
                !!(this.groupB.year?.start || this.groupB.year?.end);
        default:
            return false;
        }
    },
  },

  mounted() {
    this.syncKpiLabels();
    if (this.selectionsValid()) this.fetchComparativa();
  },
};
</script>

<style>
/* Se aplicará al menú teletransportado porque es global */
.calendar-contrast .v-date-picker-header__value button,
.calendar-contrast .v-date-picker-title__date,
.calendar-contrast .v-date-picker-title__year,
.calendar-contrast .v-date-picker-table .v-btn__content {
  color: #111 !important;
}

.calendar-contrast .v-date-picker-table .v-btn {
  color: #111 !important;
}

.calendar-contrast .v-date-picker-table th {
  color: #111 !important;
  font-weight: 600;
}

/* Mejora contraste de inputs del activador (opcional) */
.calendar-contrast .v-text-field input,
.calendar-contrast .v-label {
  color: #111 !important;
}
.kpi-card { min-height: 200px; }
.kpi-title { font-size: 18px; color: #5f6368; margin-bottom: 6px; }
.kpi-value { font-size: 44px; line-height: 1; font-weight: 800; letter-spacing: -0.5px; }
.kpi-unit { font-size: 16px; font-weight: 600; margin-left: 6px; color: #666; }
.kpi-delta { min-width: 120px; }
.kpi-delta-number { font-size: 20px; font-weight: 800; }
.kpi-delta-pct { font-size: 13px; color: #6b7280; }

/* subgrid A vs B */
.kpi-subgrid .mini-title { font-size: 12px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .04em; }
.kpi-subgrid .mini-label { font-size: 12px; color: #6b7280; }
.kpi-subgrid .mini-value { font-size: 22px; font-weight: 800; line-height: 1.1; margin-top: 2px; }
.kpi-subgrid .mini-value.current { color: #111; }
.kpi-subgrid .mini-unit { font-size: 12px; font-weight: 700; margin-left: 4px; color: #666; }
.truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Tooltip tabular (2 columnas): Normal | Tendencia */
.apexcharts-tooltip { opacity: 1; } /* mejora estabilidad visual */
.tt2 { padding: 8px 10px; max-width: 380px; }
.tt2-head { font-weight: 700; margin-bottom: 6px; }
.tt2-grid {
  display: grid;
  grid-template-columns: 1.4fr 0.9fr 0.9fr; /* Causa | Normal | Tendencia */
  column-gap: 10px;
  row-gap: 4px;
  align-items: center;
  white-space: nowrap;
}
.tt2-h { font-size: 12px; font-weight: 700; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 2px; }
.tt2-causa { font-size: 12px; font-weight: 600; color: #111827; }
.tt2-num { font-variant-numeric: tabular-nums; text-align: right; font-size: 12px; }
/* Tooltip tabular (ya lo tienes) */
.tt2-grid {
  display: grid;
  grid-template-columns: 1.4fr 0.9fr 0.9fr; /* Causa | Normal | Tendencia */
  column-gap: 10px;
  row-gap: 4px;
  align-items: center;
  white-space: nowrap;
}
/* 👇 cuando no hay tendencia, usa solo dos columnas */
.tt2-grid.cols2 {
  grid-template-columns: 1.4fr 0.9fr;       /* Causa | Normal */
}
</style>